// noinspection GrazieInspection

import { html, View } from 'rune-ts';
import { ProductImageView } from '@services/MarppleShop/renderApp/pages/Curation/components/ProductImageView.ts';
import { ProductTitleView } from '@services/MarppleShop/renderApp/pages/Curation/components/ProductTitleView.ts';
import { PriceView } from '@services/MarppleShop/renderApp/pages/Curation/components/PriceView.ts';
import klass from './CardProductView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import {
  BadgeSubView,
  TBadgeSubWithoutSize,
} from '@services/MarppleShop/renderApp/pages/Curation/components/BadgeSubView.ts';
import { ShopView } from '@services/MarppleShop/renderApp/pages/Curation/components/ShopView.ts';
import { CountViewView } from '@services/MarppleShop/renderApp/pages/Curation/components/CountViewView.ts';
import { ReviewView } from '@services/MarppleShop/renderApp/pages/Curation/components/ReviewView.ts';
import { ProductThumbnail } from '@services/MarppleShop/features/ProductList/type';
import { TBadgeMainWithoutSize } from '@services/MarppleShop/renderApp/pages/Curation/components/BadgeMainView.ts';
import { RealtimeView } from '@services/MarppleShop/renderApp/pages/Curation/components/RealtimeView.ts';
import { assert } from '@services/MarppleShop/shared/util/assert.ts';
import {
  ProductCardHelper,
  SoldOutData,
} from '@services/MarppleShop/renderApp/components/cells/ProductCard/ProductCardHelper.ts';

export class CardProductError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

type TProductImage = {
  thumbnail_info: ProductThumbnail;
  is_liked: boolean;
  is_lazy: boolean;
  resize_width: number;
  domain_name: string;
  id: number;
  name: string;
  store_name: string;
};

type TProductTitle = {
  domain_name: string;
  id: number;
  name: string;
};

type TPrice = {
  price: number;
  price_en: number | null;
  price_jp: number | null;
};

type TShop = {
  store_name: string;
  domain_name: string;
};

type TShopWithProfileImageUrl = {
  store_name: string;
  domain_name: string;
  profile_image_url: string | null;
};

type TShopWithProfileImageUrlAndWatcherCount = {
  store_name: string;
  domain_name: string;
  profile_image_url: string | null;
  watcher_count: number;
};

type TReview = {
  review_count: number;
  review_score: number;
};

type TCountView = {
  watcher_count: number;
};

type TRealtime = {
  ordered_at: Date;
};

type TBadgeMain = {
  ranking?: number;
  quantity: number;
  order_count: number;
  sell_end_at: Date | null;
  ordered_at: Date | undefined;
};

type TJustSoldTypeBadgeMain = {
  ranking?: number;
  ordered_at: Date;
};

type TBadgeSubs = {
  sell_end_at: Date | null;
  quantity: number;
  order_count: number;
};

/**
 * Type=A, State=Default, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Xsmall, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Light
 *
 * Type=A, State=Default, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Adult, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Soldout, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Soldout+Adult, Size=Xsmall, Layout=Horizontal, Theme=Dark
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Horizontal, Theme=Dark
 *
 * Type=A, State=Default, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Small, Layout=Horizontal, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Horizontal, Theme=Light
 *
 * Type=A, State=Default, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 *
 * Type=A, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=A, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=A, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 */
type TTypeAProps =
  | {
      type: 'A';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Horizontal';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Horizontal';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'A';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=B, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=B, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=B, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=B, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=B, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeBProps =
  | {
      type: 'B';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TJustSoldTypeBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TJustSoldTypeBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TJustSoldTypeBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TJustSoldTypeBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TJustSoldTypeBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TJustSoldTypeBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      realtime: TRealtime;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_main: TJustSoldTypeBadgeMain;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_main: TJustSoldTypeBadgeMain;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_main: TJustSoldTypeBadgeMain;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_main: TJustSoldTypeBadgeMain;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_main: TJustSoldTypeBadgeMain;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'B';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      badge_main: TJustSoldTypeBadgeMain;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=C, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=C, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=C, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 */
type TTypeCProps =
  | {
      type: 'C';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'C';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=D, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=D, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=D, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 */
type TTypeDProps =
  | {
      state: 'Default';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult+AgeVerification';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult+AgeVerification';
      type: 'D';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Default';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Adult+AgeVerification';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      state: 'Soldout+Adult+AgeVerification';
      type: 'D';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=E, State=Default, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult, Size=Xsmall, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Dark
 *
 * Type=E, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=E, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=E, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=E, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=E, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeEProps =
  | {
      type: 'E';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShop;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'E';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      badge_main: TBadgeMain;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=F, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=F, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=F, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=F, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=F, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeFProps =
  | {
      type: 'F';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrl;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'F';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      shop: TShopWithProfileImageUrlAndWatcherCount;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    };

/**
 * Type=G, State=Default, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult, Size=Xsmall, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Xsmall, Layout=Vertical, Theme=Light
 *
 * Type=G, State=Default, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult, Size=Small, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Small, Layout=Vertical, Theme=Dark
 *
 * Type=G, State=Default, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult, Size=Medium, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Medium, Layout=Vertical, Theme=Light
 *
 * Type=G, State=Default, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult, Size=Large, Layout=Vertical, Theme=Light
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Large, Layout=Vertical, Theme=Light
 *
 * Type=G, State=Default, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult, Size=Xlarge, Layout=Vertical, Theme=Dark
 * Type=G, State=Soldout+Adult+AgeVerification, Size=Xlarge, Layout=Vertical, Theme=Dark
 */
type TTypeGProps =
  | {
      type: 'G';
      state: 'Default';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // @TODO sws: 임시
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xsmall';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Small';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Medium';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // @TODO sws: 임시
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Large';
      layout: 'Vertical';
      theme: 'Light';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      // badge_main: TBadgeMain;
      // badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Default';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    }
  | {
      type: 'G';
      state: 'Soldout+Adult+AgeVerification';
      size: 'Xlarge';
      layout: 'Vertical';
      theme: 'Dark';
      product_image: TProductImage;
      product_title: TProductTitle;
      price: TPrice;
      review: TReview;
      count_view: TCountView;
      badge_main: TBadgeMain;
      badge_subs: TBadgeSubs;
      onProductImageLikeClick: () => void;
    };

export type TProps =
  | TTypeAProps
  | TTypeBProps
  | TTypeCProps
  | TTypeDProps
  | TTypeEProps
  | TTypeFProps
  | TTypeGProps;

/**
 * https://www.figma.com/design/J3pxvmlv167DXuLToyL3Mg/%EC%83%81%ED%92%88-%EC%B9%B4%EB%93%9C-UI?node-id=582-231284&m=dev
 */
export class CardProductView extends View<TProps> {
  // @TODO sws: getState 인터페이스 개선 (타입 좁히기)
  static getState(soldout_data: SoldOutData) {
    return ProductCardHelper.isSoldOut(soldout_data) ? 'Soldout' : 'Default';
  }

  setProductImageIsLiked(is_liked: boolean) {
    this.data.product_image.is_liked = is_liked;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.subView(ProductImageView)!.setIsLiked(is_liked);
  }

  setBadgeMain(badge_main: TBadgeMain) {
    const { type, size } = this.data;
    // @TODO sws: 임시
    // assert(
    //   (type === 'B' && size === 'Small') ||
    //     (type === 'B' && size === 'Xlarge') ||
    //     type === 'C' ||
    //     type === 'D' ||
    //     type === 'E' ||
    //     type === 'F' ||
    //     type === 'G',
    // );
    assert(
      (type === 'B' && size === 'Small') ||
        (type === 'B' && size === 'Xlarge') ||
        type === 'C' ||
        type === 'D' ||
        type === 'E' ||
        type === 'F',
      new CardProductError(),
    );

    this.data.badge_main = badge_main;
    // @TODO sws: 주석 해제
    // const badge_main_without_size = this.getBadgeMainWithoutSize();
    // this.subView(ProductImageView)!.setBadgeMainWithoutSize(badge_main_without_size);
  }

  setBadgeSubs(badge_subs: TBadgeSubs) {
    const { type } = this.data;
    // @TODO sws: 임시
    // assert(type === 'C' || type === 'D' || type === 'F' || type === 'G');
    assert(type === 'C' || type === 'D' || type === 'F', new CardProductError());

    this.data.badge_subs = badge_subs;
    const container = this.element().querySelector(`.${klass.badge_sub}`);
    const badge_subs_without_size = this.getBadgeSubsWithoutSize();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    container!.replaceChildren(
      ...badge_subs_without_size.map((badge_sub_without_size) =>
        new BadgeSubView({
          ...badge_sub_without_size,
          size:
            this.data.size === 'Large' || this.data.size === 'Xlarge'
              ? 'Large (Xlarge~Large)'
              : 'Medium (Medium~Small)',
        }).render(),
      ),
    );
  }

  private getProductImageState() {
    return this.data.state === 'Soldout' ||
      this.data.state === 'Soldout+Adult' ||
      this.data.state === 'Soldout+Adult+AgeVerification'
      ? 'Soldout'
      : 'Default';
  }

  // @TODO sws: badge main
  private getBadgeMainWithoutSize(): TBadgeMainWithoutSize | undefined {
    return undefined;
    // const { type, size } = this.data;
    //
    // if (type === 'B' && (size === 'Small' || size === 'Xlarge')) {
    //   const { ranking, ordered_at } = this.data.badge_main;
    //
    //   return {
    //     type: 'Just_sold',
    //     ranking,
    //     ordered_at,
    //   };
    // } else {
    //   return {
    //     type: 'Free_Shipping',
    //   };
    // }
  }

  // @TODO sws: badge subs
  private getBadgeSubsWithoutSize(): TBadgeSubWithoutSize[] {
    return [];
    // const { type } = this.data;
    // assert(type === 'C' || type === 'D' || type === 'F' || type === 'G');
    //
    // const {
    //   badge_subs: { quantity, order_count, sell_end_at },
    // } = this.data;
    //
    // const all_sub_badge: TBadgeSubWithoutSize[] = [
    //   {
    //     type: 'Free_Shipping',
    //   },
    //   {
    //     type: 'Time_left',
    //     sell_end_at: sell_end_at ?? new Date(),
    //   },
    //   {
    //     type: 'Stock_left',
    //     quantity,
    //     order_count,
    //   },
    //   {
    //     type: 'Popular',
    //   },
    //   {
    //     type: 'Limited',
    //     quantity,
    //   },
    //   {
    //     type: 'Trend',
    //   },
    // ];
    //
    // return fx(all_sub_badge).take(BADGE_SUBS_LIMIT).toArray();
  }

  protected override template(props: TProps) {
    if (props.type === 'A' && props.size === 'Xsmall' && props.layout === 'Horizontal') {
      const { theme, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_xsmall_layout_horizontal}">
        <div class="${klass.product_image_wrapper}">
          ${new ProductImageView({
            ...product_image,
            state: this.getProductImageState(),
            size: 'Small',
            onLikeClick: () => onProductImageLikeClick(),
          })}
        </div>

        <div class="${classes(klass.product_info, { [klass.theme_dark]: theme === 'Dark' })}">
          ${new ProductTitleView({
            ...product_title,
            size: '13px',
            adult: 'Off',
          })}
          ${new PriceView({ ...price, size: '13px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Small' && props.layout === 'Horizontal') {
      const { product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_small_layout_horizontal}">
        <div class="${klass.product_image_wrapper}">
          ${new ProductImageView({
            ...product_image,
            state: this.getProductImageState(),
            size: 'Small',
            onLikeClick: () => onProductImageLikeClick(),
          })}
        </div>

        <div class="${klass.product_info}">
          ${new ProductTitleView({ ...product_title, size: '14px', adult: 'Off' })}
          ${new PriceView({ ...price, size: '14px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Xsmall' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_xsmall_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Small',
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '12px', adult: 'Off' })}
          ${new PriceView({ ...price, size: '12px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
          ${new PriceView({ ...price, size: '14px' })}
        </div>
      </div>`;
    } else if (props.type === 'A' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_a_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
          ${new PriceView({ ...price, size: '16px' })}
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, count_view, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_b_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '11px' })}

          <div class="${klass.product_info_inner}">
            <div class="${klass.product_info_inner_inner}">
              ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>

            ${count_view.watcher_count > 0
              ? new CountViewView({
                  ...count_view,
                  size: 'Small',
                })
              : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Medium' && props.layout === 'Vertical') {
      const {
        state,
        product_image,
        shop,
        product_title,
        price,
        realtime,
        count_view,
        onProductImageLikeClick,
      } = props;

      return html`<div class="${klass.type_b_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Line', theme: 'Light', size: '11px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>

            ${new RealtimeView({
              ...realtime,
              size: 'Small',
            })}
            ${count_view.watcher_count > 0
              ? new CountViewView({
                  ...count_view,
                  size: 'Small',
                })
              : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, realtime, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_b_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Line', theme: 'Light', size: '13px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '16px' })}
            </div>

            ${new RealtimeView({
              ...realtime,
              size: 'Medium',
            })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'B' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_b_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '13px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '16px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '18px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'C' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_c_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info_detail}">
          <div
            class="${classes(klass.info, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                  )}
                </div>
              `
            : null}
        </div>
      </div>`;
    } else if (props.type === 'C' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_c_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info_detail}">
          <div
            class="${classes(klass.info, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '16px' })}
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
                  )}
                </div>
              `
            : null}
        </div>
      </div>`;
    } else if (props.type === 'D' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, count_view, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_d_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ShopView({ type: 'Text', theme: 'Light', size: '12px', ...shop })}

            <div class="${klass.product_info_detail}">
              ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                  )}
                </div>
              `
            : null}
          ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Small' }) : null}
        </div>
      </div>`;
    } else if (props.type === 'D' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, count_view, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_d_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '13px' })}

            <div class="${klass.product_info_detail}">
              ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '16px' })}
            </div>
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
                  )}
                </div>
              `
            : null}
          ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Medium' }) : null}
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Xsmall' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_xsmall_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xsmall',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '11px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '12px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '12px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '11px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '12px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div
          class="${classes(klass.product_info, {
            [klass.state_soldout]:
              state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
          })}"
        >
          ${new ShopView({ ...shop, type: 'Text', theme: 'Light', size: '13px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '16px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'E' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, onProductImageLikeClick } = props;

      return html`<div class="${klass.type_e_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${classes(klass.product_info)}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '13px' })}

          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '16px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '18px' })}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.info_wrapper}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '11px' })}

          <div class="${klass.product_info_wrapper}">
            <div class="${klass.product_info}">
              ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '15px' })}
            </div>

            ${badge_subs_without_size.length > 0
              ? html`
                  <div class="${klass.badge_sub}">
                    ${badge_subs_without_size.map(
                      (badge_sub_without_size) =>
                        new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                    )}
                  </div>
                `
              : null}
            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Dark', size: 'Small' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Light', size: '11px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '14px' })}
            </div>

            ${badge_subs_without_size.length > 0
              ? html`
                  <div class="${klass.badge_sub}">
                    ${badge_subs_without_size.map(
                      (badge_sub_without_size) =>
                        new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                    )}
                  </div>
                `
              : null}
            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Small' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Light', size: '13px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '16px' })}
            </div>

            ${badge_subs_without_size.length > 0
              ? html`
                  <div class="${klass.badge_sub}">
                    ${badge_subs_without_size.map(
                      (badge_sub_without_size) =>
                        new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
                    )}
                  </div>
                `
              : null}
            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Medium' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'F' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { state, product_image, shop, product_title, price, review, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_f_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          ${new ShopView({ ...shop, type: 'Box', theme: 'Dark', size: '13px' })}

          <div class="${klass.product_info_inner}">
            <div
              class="${classes(klass.product_info_inner_inner, {
                [klass.state_soldout]:
                  state === 'Soldout' ||
                  state === 'Soldout+Adult' ||
                  state === 'Soldout+Adult+AgeVerification',
              })}"
            >
              ${new ProductTitleView({ ...product_title, size: '16px', adult: 'Off' })}
              ${new PriceView({ ...price, size: '20px' })}
            </div>

            ${badge_subs_without_size.length > 0
              ? html`
                  <div class="${klass.badge_sub}">
                    ${badge_subs_without_size.map(
                      (badge_sub_without_size) =>
                        new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
                    )}
                  </div>
                `
              : null}
            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Dark', size: 'Medium' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Xsmall' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_g_size_xsmall_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xsmall',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '12px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '12px' })}
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                  )}
                </div>
              `
            : null}
          ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Small' }) : null}
          ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Small' }) : null}
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Small' && props.layout === 'Vertical') {
      const { product_image, product_title, price, review, count_view, onProductImageLikeClick } = props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_g_size_small_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div class="${klass.product_info_inner}">
            ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                  )}
                </div>
              `
            : null}
          ${review.review_count ? new ReviewView({ ...review, theme: 'Dark', size: 'Small' }) : null}
          ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Small' }) : null}
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Medium' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_g_size_medium_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Medium~Small',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '13px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '14px' })}
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Medium (Medium~Small)' }),
                  )}
                </div>
              `
            : null}
          ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Small' }) : null}
          ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Small' }) : null}
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Large' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_g_size_large_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '15px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '16px' })}
          </div>

          <div class="${klass.sub_wrapper}">
            ${badge_subs_without_size.length > 0
              ? html`
                  <div class="${klass.badge_sub}">
                    ${badge_subs_without_size.map(
                      (badge_sub_without_size) =>
                        new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
                    )}
                  </div>
                `
              : null}
            ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Light', size: 'Medium' }) : null}
            ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Medium' }) : null}
          </div>
        </div>
      </div>`;
    } else if (props.type === 'G' && props.size === 'Xlarge' && props.layout === 'Vertical') {
      const { state, product_image, product_title, price, review, count_view, onProductImageLikeClick } =
        props;

      const badge_subs_without_size = this.getBadgeSubsWithoutSize();

      return html`<div class="${klass.type_g_size_xlarge_layout_vertical}">
        ${new ProductImageView({
          ...product_image,
          state: this.getProductImageState(),
          size: 'Xlarge~Large',
          badge_main_without_size: this.getBadgeMainWithoutSize(),
          onLikeClick: () => onProductImageLikeClick(),
        })}

        <div class="${klass.product_info}">
          <div
            class="${classes(klass.product_info_inner, {
              [klass.state_soldout]:
                state === 'Soldout' || state === 'Soldout+Adult' || state === 'Soldout+Adult+AgeVerification',
            })}"
          >
            ${new ProductTitleView({ ...product_title, size: '16px', adult: 'Off' })}
            ${new PriceView({ ...price, size: '18px' })}
          </div>

          ${badge_subs_without_size.length > 0
            ? html`
                <div class="${klass.badge_sub}">
                  ${badge_subs_without_size.map(
                    (badge_sub_without_size) =>
                      new BadgeSubView({ ...badge_sub_without_size, size: 'Large (Xlarge~Large)' }),
                  )}
                </div>
              `
            : null}
          ${review.review_count > 0 ? new ReviewView({ ...review, theme: 'Dark', size: 'Medium' }) : null}
          ${count_view.watcher_count > 0 ? new CountViewView({ ...count_view, size: 'Medium' }) : null}
        </div>
      </div>`;
    } else {
      throw new CardProductError();
    }
  }
}
